import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../store/AppStore';
import { closeDialog } from '../store/states/DialogSlice';
import DraggableComponent from './DraggableComponent';
import { ECommonDialogType } from '../data/Constants';
import { useTranslation } from 'react-i18next';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { MultiNumericManipulation } from './category/components/MultinumericFeatureManipulation';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * returns the dialog title for different type
 * @param {ECommonDialogType} type is used to get specific title of Dialog
 * @returns {string} Title of Dialog box
 */
const getDialogTitle = (type: ECommonDialogType, t: (key: string) => string, title: string) => {
  switch (type) {
    case ECommonDialogType.Error:
      return (
        <div className="d-flex">
          <CancelOutlinedIcon color="error" className="icon-font-size" />
          {t('dialogBox.error')}
        </div>
      );
    case ECommonDialogType.Success:
      return t('dialogBox.success');
    case ECommonDialogType.Confirm:
      return (
        <div className="d-flex">
          <WarningIcon className="warning-icon" /> &nbsp;<div>{title}</div>
        </div>
      );
    default:
      return title ?? '< Unknown >';
  }
};

/**
 * returns the dialog content for different type
 * @param {object} param is an object containing data
 * @returns {React.JSX.Element} returns HTML element
 */
const getDialogContent = (param: { type: ECommonDialogType; dialogData: any }) => {
  const message = param?.dialogData?.message ?? param?.dialogData;

  switch (param.type) {
    case ECommonDialogType.Error:
      return (
        <div className="d-flex">
          <CancelOutlinedIcon color="error" className="icon-font-size" />
          {message}
        </div>
      );
    case ECommonDialogType.Success:
      return (
        <div className="d-flex">
          <CheckCircleOutlineIcon color="success" className="icon-font-size" />
          {message}
        </div>
      );
    case ECommonDialogType.MultiNumericManipulation:
      return (
        <MultiNumericManipulation action={param.dialogData.action} id={param.dialogData.code} />
      );
    default:
      return message ?? 'Dialog';
  }
};

//return the action buttons
const getDialogActions = (
  type: ECommonDialogType,
  closeDialogWindow,
  handleClickOk,
  t: (key: string) => string
) => {
  switch (type) {
    case ECommonDialogType.Confirm:
      return (
        <>
          <Button className="text-capitalize" onClick={() => closeDialogWindow()}>
            {t('button.cancel')}
          </Button>
          <Button variant="contained" onClick={() => handleClickOk()}>
            {t('button.ok')}
          </Button>
        </>
      );
    case ECommonDialogType.MultiNumericManipulation:
      return null;
    default:
      return <Button onClick={() => handleClickOk()}>{t('button.ok')}</Button>;
  }
};

/**
 * This component is to show the Common dialog for different warnings and message
 * @returns {JSX.Element}  a CommonDialog component
 */
const CommonDialog = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const dialog = useSelector((state: AppState) => state.dialog);

  //dispatch th closediaglog action to close the dialog box
  const closeDialogWindow = () => {
    dispatch(closeDialog());
  };

  //triggers the action while click on OK button for different type.
  const handleClickOk = () => {
    //closes the dialog
    dispatch(closeDialog());
    //specific action on click of ok
    dialog.handleClickOk(dialog.data);
  };

  return dialog.show ? (
    <Dialog
      open={dialog.show}
      aria-labelledby="draggable-dialog-title"
      PaperComponent={DraggableComponent}
      className={`common-dialog-style alert-dialog ${dialog?.data?.action ? 'manipulation-dialog' : ''} `}
    >
      <DialogTitle className="header cursor-move">
        {getDialogTitle(dialog.type, t, dialog.title)}
      </DialogTitle>

      <DialogContent className="p-15px text-left ">
        {getDialogContent({ type: dialog.type, dialogData: dialog.data })}
      </DialogContent>

      <DialogActions>
        {getDialogActions(dialog.type, closeDialogWindow, handleClickOk, t)}
      </DialogActions>
    </Dialog>
  ) : (
    <> </>
  );
};

export default CommonDialog;
